import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { FaSave, FaMoneyBillWave, FaUniversity, FaCalendarAlt, FaReceipt, FaComment } from 'react-icons/fa';
import './deposit.css';

const bankOptions = ['CBE', 'Awash', 'Dashen', 'Abyssinia', 'Birhan', 'Telebirr'];

const Deposit = () => {
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [bankName, setBankName] = useState('CBE');
  const [accountNumber, setAccountNumber] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please log in to make a deposit.');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }

    setError(null);
    setLoading(true);
    
    try {
      const userId = user.id || user.user_id || user.sub;
      if (!userId) {
        throw new Error('User information is missing. Please log in again.');
      }

      const depositData = {
        user_id: userId,
        organization_id: user.organization_id || 'default_org', // Provide a default or handle as needed
        bank_name: bankName,
        account_number: accountNumber,
        amount: parseFloat(amount),
        deposit_date: depositDate,
        reference_number: referenceNumber,
        comment: comment || '',
      };

      // Format request similar to other web components
      const response = await axios.post(
        `${ENDPOINT}?action=add_bank_deposit`,
        depositData
      );

      const responseData = response.data;

      if (responseData.success) {
        // Clear form
        setAmount('');
        setBankName('CBE');
        setAccountNumber('');
        setReferenceNumber('');
        setComment('');
        toast.success('Bank deposit recorded successfully!');
      } else {
        setError(responseData.message || 'Failed to record deposit.');
        toast.error(responseData.message || 'Failed to record deposit.');
      }
    } catch (err) {
      console.error("Error recording deposit:", err);
      const errorMessage = err.response?.data?.message || 'Network Error: Could not connect to the server.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderErrorState = () => (
    <div className="error-container">
      <div className="error-icon">
        <FaMoneyBillWave size={50} color="red" />
      </div>
      <p className="error-message">{error}</p>
      <button
        className="retry-button"
        onClick={() => setError(null)}
      >
        <FaSave size={20} style={{ marginRight: '5px' }} />
        Try Again
      </button>
    </div>
  );

  return (
    <div className="deposit-page">
      <h2 className="page-title">Record Bank Deposit</h2>
      
      {error ? (
        renderErrorState()
      ) : (
        <div className="deposit-form-container">
          <form className="deposit-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Bank Name</label>
              <div className="bank-options">
                {bankOptions.map(bank => (
                  <button
                    type="button"
                    key={bank}
                    className={`bank-option ${bankName === bank ? 'selected' : ''}`}
                    onClick={() => setBankName(bank)}
                  >
                    {bank}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="amount">
                <FaMoneyBillWave className="input-icon" />
                Amount
              </label>
              <input
                id="amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="accountNumber">
                <FaUniversity className="input-icon" />
                Account Number (Optional)
              </label>
              <input
                id="accountNumber"
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="Enter account number"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="depositDate">
                <FaCalendarAlt className="input-icon" />
                Deposit Date
              </label>
              <input
                id="depositDate"
                type="date"
                value={depositDate}
                onChange={(e) => setDepositDate(e.target.value)}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="referenceNumber">
                <FaReceipt className="input-icon" />
                Reference Number (Optional)
              </label>
              <input
                id="referenceNumber"
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Enter reference number"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="comment">
                <FaComment className="input-icon" />
                Comment (Optional)
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment"
                className="form-textarea"
                rows="3"
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading || !amount || parseFloat(amount) <= 0}
            >
              {loading ? 'Recording...' : 'Record Deposit'}
              {!loading && <FaSave className="button-icon" />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Deposit;